import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconArrowRight, IconChevronDown } from '@tabler/icons-react';
import { lockPageScroll } from './use-smooth-scroll';
import { ThemeSwitch, useThemedPage } from './marketing-theme';
import { NAV_SECTIONS, NAV_PRICING } from './nav-data';

const EXIT_MS = 380;
const DRAG_CLOSE_PX = 90;

export function MobileMenuDrawer({ open, onClose, signedIn }: { open: boolean; onClose: () => void; signedIn: boolean }) {
  const themedPage = useThemedPage();
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startY: number; startT: number; dy: number } | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    lockPageScroll(true);
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const focusables = Array.from(sheetRef.current.querySelectorAll<HTMLElement>('a[href], button')).filter((el) => el.offsetParent !== null);
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const focusT = setTimeout(() => sheetRef.current?.querySelector<HTMLElement>('button, a[href]')?.focus({ preventScroll: true }), 120);
    return () => {
      lockPageScroll(false);
      document.removeEventListener('keydown', onKey);
      clearTimeout(focusT);
      opener?.focus({ preventScroll: true });
    };
  }, [open, onClose]);

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { startY: e.clientY, startT: performance.now(), dy: 0 };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current, sheet = sheetRef.current;
    if (!d || !sheet) return;
    d.dy = Math.max(0, e.clientY - d.startY);
    sheet.style.transition = 'none';
    sheet.style.transform = `translateY(${d.dy}px)`;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current, sheet = sheetRef.current;
    drag.current = null;
    if (!d || !sheet) return;
    d.dy = Math.max(d.dy, e.clientY - d.startY);
    const velocity = d.dy / Math.max(1, performance.now() - d.startT);
    sheet.style.transition = '';
    sheet.style.transform = '';
    if (d.dy > DRAG_CLOSE_PX || (d.dy > 24 && velocity > 0.6)) onClose();
  };

  if (!mounted) return null;

  return (
    <div className="mm-root lg:hidden fixed inset-0 z-50" data-state={shown ? 'open' : 'closed'}>
      <div className="mm-backdrop absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="mm-sheet absolute inset-x-0 bottom-0 rounded-t-[28px] px-4 pt-2 flex flex-col"
        style={{ background: 'var(--warm-page)', maxHeight: '92dvh', paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <div
          className="flex justify-center pt-1 pb-3 cursor-grab touch-none shrink-0"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="block w-10 h-1.5 rounded-full" style={{ background: 'var(--warm-handle)' }} />
        </div>

        <nav aria-label="Site" className="flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-1 px-1" data-lenis-prevent>
          <ul className="space-y-2">
            {NAV_SECTIONS.map((s, idx) => {
              const isOpen = expanded === s.id;
              return (
                <li
                  key={s.id}
                  className="mm-tile rounded-2xl overflow-hidden"
                  style={{ '--i': idx, background: 'var(--warm-card)', border: '1px solid var(--warm-border)' } as React.CSSProperties}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`mm-${s.id}`}
                    onClick={() => setExpanded(isOpen ? null : s.id)}
                    className="w-full min-h-[52px] flex items-center justify-between px-4 text-left"
                    style={{ color: 'var(--warm-text)' }}
                  >
                    <span className="font-display text-xl">{s.label}</span>
                    <IconChevronDown size={18} stroke={1.75} className="transition-transform duration-200" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', color: 'var(--warm-muted)' }} />
                  </button>
                  {isOpen && (
                    <div id={`mm-${s.id}`} className="px-2 pb-3" style={{ borderTop: '1px solid var(--warm-border)' }}>
                      {s.groups.map((g) => (
                        <div key={g.title} className="pt-3">
                          <p className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--warm-muted)' }}>{g.title}</p>
                          <ul>
                            {g.items.map((it) => (
                              <li key={it.to + it.label}>
                                <Link
                                  to={it.to}
                                  onClick={onClose}
                                  aria-current={pathname === it.to ? 'page' : undefined}
                                  className="flex items-center gap-3 rounded-xl px-2.5 min-h-[48px] py-1.5 active:bg-[var(--warm-inset)]"
                                >
                                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'var(--warm-inset)', color: 'var(--warm-text)' }}>
                                    <it.icon size={17} stroke={1.5} />
                                  </span>
                                  <span className="min-w-0">
                                    <span className="block text-sm font-medium leading-5" style={{ color: 'var(--warm-text)' }}>{it.label}</span>
                                    <span className="block text-xs leading-4" style={{ color: 'var(--warm-muted)' }}>{it.desc}</span>
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                      {s.feature && (
                        <Link
                          to={s.feature.to}
                          onClick={onClose}
                          className="mt-3 flex items-center justify-between gap-3 rounded-xl p-3.5"
                          style={{ background: 'var(--warm-inset)', border: '1px solid var(--warm-border)' }}
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-medium" style={{ color: 'var(--warm-text)' }}>{s.feature.title}</span>
                            <span className="block text-xs leading-4 mt-0.5" style={{ color: 'var(--warm-muted)' }}>{s.feature.body}</span>
                          </span>
                          <IconArrowRight size={16} stroke={1.75} className="shrink-0" style={{ color: 'var(--warm-text)' }} />
                        </Link>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
            <li
              className="mm-tile rounded-2xl"
              style={{ '--i': NAV_SECTIONS.length, background: 'var(--warm-card)', border: `1px solid ${pathname === NAV_PRICING.to ? 'var(--warm-text)' : 'var(--warm-border)'}` } as React.CSSProperties}
            >
              <Link to={NAV_PRICING.to} onClick={onClose} className="min-h-[52px] flex items-center justify-between px-4" style={{ color: 'var(--warm-text)' }}>
                <span className="font-display text-xl">{NAV_PRICING.label}</span>
                <span className="text-xs" style={{ color: 'var(--warm-muted)' }}>{NAV_PRICING.hint}</span>
              </Link>
            </li>
          </ul>
        </nav>

        <div className="shrink-0">
          {themedPage && (
            <div className="mm-tile mt-3 flex items-center justify-between" style={{ '--i': 6 } as React.CSSProperties}>
              <span className="text-sm" style={{ color: 'var(--warm-muted)' }}>Theme</span>
              <ThemeSwitch />
            </div>
          )}

          <div className="mm-tile mt-3 flex gap-2.5" style={{ '--i': 7 } as React.CSSProperties}>
            {signedIn ? (
              <Link to="/dashboard" onClick={onClose} className="warm-btn flex-1 inline-flex items-center justify-center gap-1.5 py-3.5 text-sm">
                Go to app <IconArrowRight size={16} stroke={1.75} />
              </Link>
            ) : (
              <>
                <Link to="/auth" onClick={onClose} className="flex-1 inline-flex items-center justify-center py-3.5 text-sm rounded-[10px] font-medium" style={{ border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>
                  Log in
                </Link>
                <Link to="/auth" onClick={onClose} className="warm-btn flex-[1.4] inline-flex items-center justify-center gap-1.5 py-3.5 text-sm">
                  Start free <IconArrowRight size={16} stroke={1.75} />
                </Link>
              </>
            )}
          </div>
          {!signedIn && (
            <p className="mm-tile mt-3 text-center text-xs" style={{ color: 'var(--warm-muted)', '--i': 8 } as React.CSSProperties}>
              1,100 free credits · No credit card
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
